import { useState, useRef, useEffect } from "react";
import pdfToText from "react-pdftotext";
import {
  Button,
  TextField,
  Typography,
  Slider,
  Select,
  MenuItem,
  Box,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  LinearProgress,
  Chip,
  IconButton,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import Tooltip from "@mui/material/Tooltip";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadIcon from "@mui/icons-material/Upload";
import ReviewDisplay from "./ReviewDisplay";
import { motion } from "framer-motion";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import FormData from "form-data";
import getIdentity from "../../services/getIdentity";

// Configure pdf.js worker
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// File validation constants
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ALLOWED_FILE_TYPES = ["application/pdf"];

const PINATA_JWT = process.env.REACT_APP_PINATA_JWT;

const LabelTypography = ({ children }) => (
  <Typography
    component="label"
    sx={{
      fontSize: "0.975rem",
      color: "#000000",
      mb: 1,
      display: "block",
    }}
  >
    {children}
  </Typography>
);

const calculatePages = (wordCount) => {
  const WORDS_PER_PAGE = 300;
  return Math.ceil(wordCount / WORDS_PER_PAGE);
};

const uploadToPinata = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      "https://api.pinata.cloud/pinning/pinFileToIPFS",
      formData,
      {
        headers: {
          Authorization: `Bearer ${PINATA_JWT}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return `https://gateway.pinata.cloud/ipfs/${response.data.IpfsHash}`;
  } catch (error) {
    console.error("Error uploading to Pinata:", error);
    throw new Error("Failed to upload file to Pinata");
  }
};

export default function LiteratureReviewGenerator() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    about: "",
    wordCount: 3500,
    citationStyle: "APA",
    paperCount: 3,
  });
  const [survey, setSurvey] = useState(null);
  const [references, setReferences] = useState(null);
  const reviewRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecordingPermission, setHasRecordingPermission] = useState(null);
  const recognition = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedMethod, setSelectedMethod] = useState("system"); // 'system' or 'upload'
  const fileInputRef = useRef(null);
  const [abortController, setAbortController] = useState(null);

  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.webkitSpeechRecognition || window.SpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;

      recognition.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join("");

        setFormData((prev) => ({
          ...prev,
          about: transcript,
        }));
      };

      recognition.current.onerror = (event) => {
        setError(`Speech recognition error: ${event.error}`);
        setIsRecording(false);
      };
    }
  }, []);

  const toggleRecording = async () => {
    try {
      if (!isRecording) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        setHasRecordingPermission(true);

        recognition.current.start();
        setIsRecording(true);
      } else {
        recognition.current.stop();
        setIsRecording(false);
      }
    } catch (err) {
      setError(
        "Microphone permission denied. Please enable microphone access to use voice input."
      );
      setHasRecordingPermission(false);
    }
  };

  const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      throw new Error("Only PDF files are allowed");
    }
    if (file.size > MAX_FILE_SIZE) {
      throw new Error("File size should not exceed 10MB");
    }
  };

  const extractTextFromPdf = async (file) => {
    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (event) => {
          try {
            const text = await pdfToText(file);

            resolve(text);
          } catch (error) {
            reject(new Error("Failed to extract text from PDF"));
          }
        };

        reader.onerror = () => {
          reject(new Error("Failed to read file"));
        };

        reader.readAsArrayBuffer(file);
      });
    } catch (error) {
      console.error("PDF extraction error:", error);
      throw error;
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      try {
        validateFile(file);

        // Immediately add file to uploadedFiles with initial state
        setUploadedFiles((prev) => [
          ...prev,
          {
            name: file.name,
            size: file.size,
            text: "", // Will be updated after extraction
            pinataUrl: "", // Will be updated after upload
          },
        ]);

        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 0,
        }));

        // Start progress animation
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 30,
        }));

        // Upload to Pinata first
        const pinataUrl = await uploadToPinata(file);

        // Extract text from PDF
        const text = await extractTextFromPdf(file);

        // Update progress
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 60,
        }));

        // Update the existing file entry with the extracted text and pinataUrl
        setUploadedFiles((prev) =>
          prev.map((existingFile) =>
            existingFile.name === file.name
              ? {
                  ...existingFile,
                  text: text,
                  pinataUrl: pinataUrl,
                }
              : existingFile
          )
        );

        // Complete progress
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 100,
        }));
      } catch (error) {
        setError(`Error processing ${file.name}: ${error.message}`);
        // Remove the file if there was an error
        setUploadedFiles((prev) =>
          prev.filter((existingFile) => existingFile.name !== file.name)
        );
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer.files);
    const pdfFiles = files.filter((file) =>
      ALLOWED_FILE_TYPES.includes(file.type)
    );

    if (pdfFiles.length === 0) {
      setError("Please drop PDF files only");
      return;
    }

    for (const file of pdfFiles) {
      try {
        validateFile(file);
        await handleFileUpload({ target: { files: [file] } });
      } catch (error) {
        setError(`Error processing ${file.name}: ${error.message}`);
      }
    }
  };

  const handleRemoveFile = (fileName) => {
    setUploadedFiles((prev) => prev.filter((file) => file.name !== fileName));
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  };

  const handleMethodChange = (method) => {
    setSelectedMethod(method);
    if (method === "system") {
      setUploadedFiles([]);
      setUploadProgress({});
    }
  };

  const handleCancel = () => {
    if (abortController) {
      abortController.abort();
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Create new AbortController for this request
    const controller = new AbortController();
    setAbortController(controller);

    try {
      // Get browser fingerprint
      const userIdentifier = await getIdentity();

      // Input validation
      if (!formData.title.trim()) {
        throw new Error("Title is required");
      }

      if (!formData.about.trim()) {
        throw new Error("Description is required");
      }

      if (selectedMethod === "upload" && uploadedFiles.length === 0) {
        throw new Error("Please upload at least one paper");
      }

      // Prepare paper URLs instead of texts
      const paperUrls =
        selectedMethod === "upload"
          ? uploadedFiles.map((file) => file.pinataUrl).filter(Boolean)
          : [];

      const requestPayload = {
        title: formData.title.trim(),
        about: formData.about.trim(),
        paper_count:
          selectedMethod === "system" ? formData.paperCount : paperUrls.length,
        paper_urls: paperUrls, // Send URLs instead of texts
        use_system_papers: selectedMethod === "system",
        citation_style: formData.citationStyle,
        desired_word_count: formData.wordCount,
        timestamp: Date.now(),
      };

      // Validate word count
      if (
        requestPayload.desired_word_count < 1000 ||
        requestPayload.desired_word_count > 3500
      ) {
        throw new Error("Word count must be between 1000 and 3500");
      }

      // Make API request with direct payload
      const response = await fetch(
        "https://lit-api-3g5x.onrender.com/api/literature-survey",
        // "http://localhost:8080/api/literature-survey",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Identifier": userIdentifier, // Add browser fingerprint as header
          },
          body: JSON.stringify(requestPayload),
          signal: controller.signal,
        }
      );

      const surveyData = await response.json();

      // Handle different types of API responses
      if (!response.ok) {
        throw new Error(surveyData.error || "Failed to generate survey");
      }

      if (!surveyData.survey) {
        throw new Error("Invalid response from server");
      }

      // Update the separate states
      setSurvey(surveyData.survey.content);
      setReferences(surveyData.survey.references);

      // Scroll to review section
      if (reviewRef.current) {
        reviewRef.current.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      // Don't show error if request was cancelled
      if (error.name === "AbortError") {
        return;
      }

      console.error("Error:", error);
      const errorMessage =
        error.message === "Failed to fetch"
          ? "Unable to connect to the server. Please check your internet connection."
          : error.message;

      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsLoading(false);
      setAbortController(null);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  const renderFileUploadArea = () => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        border: "2px dashed rgba(0, 0, 0, 0.12)",
        cursor: "pointer",
        "&:hover": { borderColor: "primary.main" },
        transition: "border-color 0.3s ease",
      }}
      onClick={() => fileInputRef.current?.click()}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Box sx={{ textAlign: "center" }}>
        <UploadIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
        <Typography variant="body1">
          Click to upload PDF files or drag and drop
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Maximum file size: 20MB
        </Typography>
      </Box>
    </Paper>
  );

  const renderFileList = () =>
    uploadedFiles.length > 0 && (
      <List>
        {uploadedFiles.map((file) => (
          <ListItem
            key={file.name}
            sx={{
              bgcolor: "background.paper",
              mb: 1,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              flexWrap: "wrap",
              gap: 1,
              py: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: "100%", sm: "auto" },
                flex: 1,
                minWidth: 0, // This enables text truncation
              }}
            >
              <PictureAsPdfIcon
                sx={{ mr: 1, color: "primary.main", flexShrink: 0 }}
              />
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Typography>
                }
                secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                sx={{ minWidth: 0 }} // This enables text truncation
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                ml: { xs: 4, sm: 2 },
                mt: { xs: 1, sm: 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {uploadProgress[file.name] < 100 ? (
                <Box sx={{ width: "100px", flexShrink: 0 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress[file.name]}
                  />
                </Box>
              ) : (
                <Chip
                  label="Processed"
                  color="success"
                  size="small"
                  sx={{ flexShrink: 0 }}
                />
              )}
              <IconButton
                disabled={isLoading}
                onClick={() => handleRemoveFile(file.name)}
                aria-label="delete file"
                sx={{ flexShrink: 0 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </ListItem>
        ))}
      </List>
    );

  const renderPaperSelectionControls = () => (
    <Box sx={{ mb: 3 }}>
      <LabelTypography>Paper Source</LabelTypography>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant={selectedMethod === "system" ? "contained" : "outlined"}
          onClick={() => handleMethodChange("system")}
          startIcon={<SearchIcon />}
          fullWidth
          disabled={isLoading}
        >
          Use System Search
        </Button>
        <Button
          variant={selectedMethod === "upload" ? "contained" : "outlined"}
          onClick={() => handleMethodChange("upload")}
          startIcon={<UploadIcon />}
          fullWidth
          disabled={isLoading}
        >
          Upload Papers
        </Button>
      </Stack>

      {selectedMethod === "system" && (
        <Box sx={{ mb: 2 }}>
          <LabelTypography>Number of Research Papers</LabelTypography>
          <Stack spacing={2} direction="row" alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Slider
                value={formData.paperCount}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, paperCount: newValue })
                }
                min={1}
                max={20}
                step={1}
                marks
                valueLabelDisplay="auto"
                disabled={isLoading}
              />
            </Box>
            <Typography variant="body2" sx={{ minWidth: 80 }}>
              {formData.paperCount}{" "}
              {formData.paperCount === 1 ? "paper" : "papers"}
            </Typography>
          </Stack>
        </Box>
      )}

      {selectedMethod === "upload" && (
        <Box>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".pdf"
            multiple
            style={{ display: "none" }}
          />
          {renderFileUploadArea()}
          {renderFileList()}
        </Box>
      )}
    </Box>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          py: { xs: 2, md: 3 },
          px: { xs: 2, md: 3 },
        }}
      >
        <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4, md: 5 } }}>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={2}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 3,
              background: "linear-gradient(145deg, #F3E8FF, #FFFFFF)",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              color="primary"
              align="center"
              gutterBottom
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
              }}
            >
              AI Literature Review Generator
            </Typography>

            <Stack spacing={2.5}>
              <Box>
                <LabelTypography>Title</LabelTypography>
                <TextField
                  fullWidth
                  size="small"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                  placeholder="Enter the title of your literature review"
                  InputProps={{
                    sx: {
                      "& input::placeholder": {
                        fontSize: "0.875rem",
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "#FFFFFF",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#FFFFFF",
                      },
                    },
                  }}
                  disabled={isLoading}
                />
              </Box>

              <Box>
                <LabelTypography>Description</LabelTypography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    value={formData.about}
                    onChange={(e) =>
                      setFormData({ ...formData, about: e.target.value })
                    }
                    required
                    placeholder="Describe the focus of your literature review"
                    InputProps={{
                      sx: {
                        "& textarea::placeholder": {
                          fontSize: "0.875rem",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        "&:hover": {
                          backgroundColor: "#FFFFFF",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#FFFFFF",
                        },
                      },
                    }}
                    disabled={isLoading}
                  />
                  <Tooltip
                    title={isRecording ? "Stop Recording" : "Start Voice Input"}
                  >
                    <IconButton
                      onClick={toggleRecording}
                      color={isRecording ? "error" : "primary"}
                      sx={{
                        animation: isRecording ? "pulse 1.5s infinite" : "none",
                        "@keyframes pulse": {
                          "0%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                          "100%": { opacity: 1 },
                        },
                      }}
                    >
                      {isRecording ? <MicIcon /> : <MicOffIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box>
                <LabelTypography>Citation Style</LabelTypography>
                <Select
                  value={formData.citationStyle}
                  onChange={(e) =>
                    setFormData({ ...formData, citationStyle: e.target.value })
                  }
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiSelect-select": {
                      fontSize: "0.875rem",
                    },
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    "&:hover": {
                      backgroundColor: "#FFFFFF",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#FFFFFF",
                    },
                  }}
                  disabled={isLoading}
                >
                  <MenuItem value="APA" sx={{ fontSize: "0.875rem" }}>
                    APA
                  </MenuItem>
                  <MenuItem value="MLA" sx={{ fontSize: "0.875rem" }}>
                    MLA
                  </MenuItem>
                  <MenuItem value="Chicago" sx={{ fontSize: "0.875rem" }}>
                    Chicago
                  </MenuItem>
                </Select>
              </Box>

              {renderPaperSelectionControls()}

              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                    fontWeight: 600,
                  }}
                >
                  {isLoading ? "Generating..." : "Generate Review"}
                </Button>

                {isLoading && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancel}
                    startIcon={<CancelIcon />}
                    sx={{
                      py: 1.5,
                      minWidth: "120px",
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>

          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseError}
              severity="error"
              variant="filled"
              sx={{ width: "100%" }}
            >
              {error}
            </Alert>
          </Snackbar>
        </Container>

        <ReviewDisplay
          ref={reviewRef}
          survey={survey}
          references={references}
          isLoading={isLoading}
          metadata={formData}
        />
      </Box>
    </motion.div>
  );
}
